/**
 * @license
 * Copyright 2019 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
//import * as Posenet from '@tensorflow-models/posenet';
//import * as tf from '@tensorflow/tfjs-core';
//import { tupleValuesAreOne } from '@tensorflow/tfjs-core/dist/ops/conv_util';
import * as tf from '@tensorflow/tfjs';
import { INFO,
         CONSTS } from './object';

const boundingBoxColor = 'red';
const lineWidth = 2;

export const tryResNetButtonName = 'tryResNetButton';
export const tryResNetButtonText = '[New] Try ResNet50';
const tryResNetButtonTextCss = 'width:100%;text-decoration:underline;';
const tryResNetButtonBackgroundCss = 'background:#e61d5f;';

function isAndroid() {
    return /Android/i.test(navigator.userAgent);
}

function isiOS() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function isMobile() {
    return isAndroid() || isiOS();
}

function setDatGuiPropertyCss(propertyText, liCssString, spanCssString = '') {
    var spans = document.getElementsByClassName('property-name');
    for (var i = 0; i < spans.length; i++) {
        var text = spans[i].textContent || spans[i].innerText;
        if (text == propertyText) {
            spans[i].parentNode.parentNode.style = liCssString;
            if (spanCssString !== '') {
                spans[i].style = spanCssString;
            }
        }
    }
}

export function updateTryResNetButtonDatGuiCss() {
    setDatGuiPropertyCss(
            tryResNetButtonText, tryResNetButtonBackgroundCss,
            tryResNetButtonTextCss);
}

/**
 * Toggles between the loading UI and the main canvas UI.
 */
export function toggleLoadingUI(showLoadingUI, loadingDivId = 'loading', mainDivId = 'video_container') {
    const loading = document.getElementById(loadingDivId);
    if (showLoadingUI) {
        if (loading !== null) {
            loading.style.display = 'block';
            document.getElementById(mainDivId).style.display = 'none';
        }
    } else {
        if (loading !== null) {
            loading.style.display = 'none';
            document.getElementById(mainDivId).style.display = 'block';
        }
    }
}

function toTuple({y, x}) {
    return [y, x];
}

export function drawPoint(ctx, y, x, r, color) {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
}

/**
 * Draws a line on a canvas, i.e. a joint
 */
export function drawSegment([ay, ax], [by, bx], color, scale, ctx, lineWidth) {
    //console.log("drawSegment.93: ***** pose ctx=", ctx, " lineWidth=", lineWidth)
    ctx.beginPath();
    ctx.moveTo(ax * scale, ay * scale);
    ctx.lineTo(bx * scale, by * scale);
    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = color;
    ctx.stroke();
}

/**
 * Draws a pose skeleton by looking up all adjacent keypoints/joints
 */
export function drawSkeleton(keypoints, minConfidence, ctx, lineWidth, color, scale = 1) {
    //console.log("drawSkeleton.106: minConf=", minConfidence, " keypoints=", keypoints);
    const adjacentKeyPoints = {}
            //Posenet.getAdjacentKeyPoints(keypoints, minConfidence);

    //console.log("109:  START new skeleton");
    adjacentKeyPoints.forEach((keypoints) => {
        var k0 = keypoints[0];
        var k1 = keypoints[1];
        var s0 = Math.floor(keypoints[0].score*100);
        var s1 = Math.floor(keypoints[1].score*100);
        var ncolor = color;
        if((s0 < 70) || (s1 < 70)) {
            //ncolor = "red";
        }
        //console.log("110:            skeleton ",p0,"  (",Math.floor(k0.x), Math.floor(k0.y), " - ", s0, 
        //            ") => ",p1," (", Math.floor(k1.x), Math.floor(k1.y)," - ", s1, ")")
        if((s0 > 70) && (s1 > 70)) {
            drawSegment(
                [k0.y, k0.x], [k1.y, k1.x], ncolor, scale, ctx, lineWidth);
                //toTuple(k0), toTuple(k1), ncolor, scale, ctx, lineWidth);
        }
    });
}

// obscure player facial features to preserve privacy
//
// src: https://www.tensorflow.org/lite/examples/pose_estimation/overview
export function drawFaceMask(keypoints, ctx) {
	var nose = keypoints[INFO.idx["nose"]];		// nose
	var reye = keypoints[INFO.idx["right_eye"]];		// right eye
	var leye = keypoints[INFO.idx["left_eye"]];		// left eye
    var score = (nose.score + reye.score + leye.score) / 3;
    //console.log("draw.145:          mask.score=",score, "  leye=",leye);
    if (score < 0.3) {
        return;
    }

    var w_offset = Math.abs(leye.x - reye.x) * 1.1;     // 0.8
    var h_offset = Math.abs(nose.y - ((leye.y + reye.y)/2)) * 2.5;  // 2.2
    //var h_offset = w_offset / 2;

    //console.log("draw.145:          mask.score=",score, "  w_offset=",w_offset, " h_offset=",h_offset);
    var x0 = leye.x - w_offset;
    var y0 = leye.y - h_offset;
    var x1 = reye.x + w_offset;
    var y1 = reye.y + h_offset;
    var w = x1 - x0;
    var h = y1 - y0;
    var color = CONSTS.COLOR_FACE_MASK;     //'#b7b5a3dd'

    if ((w_offset > 0) && (h_offset > 0)) {
        //console.log("draw.149:          RENDER MASK  n.x=",nose.x, " n.y=",nose.y,"  leye.y=",leye.y,"  reye.y=",reye.y, " w.off=",w_offset, " h.off=",h_offset);
        ctx.beginPath();       // Start a new path
        ctx.fillStyle = color;
        ctx.strokeStyle = color;
        ctx.lineWidth = 0;
        ctx.ellipse(nose.x, nose.y, w_offset, h_offset, 0, 0, Math.PI*2);
        ctx.closePath();
        ctx.fill();
        //ctx.stroke();

        //ctx.fillRect(x0, y0, w, h);
        //ctx.putImageData(imageData, 0, 0);
    }
}


/**
 * Draw pose keypoints onto a canvas
 */
export function drawKeypoints(keypoints, minConfidence, ctx, color, scale = 1) {
    for (let i = 0; i < keypoints.length; i++) {
        const keypoint = keypoints[i];

        if (keypoint.score < minConfidence) {
            continue;
        }

        //const {y, x} = keypoint.position;
        const x = keypoint.x;
        const y = keypoint.y;
        //drawPoint(ctx, y * scale, x * scale, 3, color);
        drawPoint(ctx, y * scale, x * scale, 1, color);
    }
}



/**
 * Draw the bounding box of a pose. For example, for a whole person standing
 * in an image, the bounding box will begin at the nose and extend to one of
 * ankles
 */
export function drawBoundingBox(keypoints, ctx) {
    //const boundingBox = Posenet.getBoundingBox(keypoints);
    const boundingBox = {};

    ctx.rect(
            boundingBox.minX, boundingBox.minY, boundingBox.maxX - boundingBox.minX,
            boundingBox.maxY - boundingBox.minY);

    ctx.strokeStyle = boundingBoxColor;
    ctx.stroke();
}

/**
 * Converts an arary of pixel data into an ImageData object
 */
export async function renderToCanvas(a, ctx) {
    console.log("rednderToCanvas.152: reached a=", a);
    const [height, width] = a.shape;
    const imageData = new ImageData(width, height);

    const data = await a.data();

    for (let i = 0; i < height * width; ++i) {
        const j = i * 4;
        const k = i * 3;

        imageData.data[j + 0] = data[k + 0];
        imageData.data[j + 1] = data[k + 1];
        imageData.data[j + 2] = data[k + 2];
        imageData.data[j + 3] = 255;
    }

    ctx.putImageData(imageData, 0, 0);
}

/**
 * Copy last result to output canvas
 */
export function renderImageToCanvas(image, size, canvas) {
    //console.log("renderImageToCanvas.175:  ! ! ! ! ! ! ! ! ! !  size=", size, " image=", image, "  canvas=", canvas);
    if((canvas) && (image) && (canvas.id !== image.id)) {
            canvas.width = size[0];
            canvas.height = size[1];
            const ctx = canvas.getContext('2d');

            //ctx.clearRect(0, 0, canvas.width, canvas.height);
            //console.log("renderImageToCanvas.182: DRAWING x=",size[0], " y=",size[1]);


            ctx.drawImage(image, 0, 0, size[0], size[1]);
            //ctx.drawImage(image, 0, 0, size[0], size[1]);
    } else {
        console.log("renderImageToCanvas.185: null canvas");
    }
}

/**
 * Draw heatmap values, one of the model outputs, on to the canvas
 * Read our blog post for a description of PoseNet's heatmap outputs
 * https://medium.com/tensorflow/real-time-human-pose-estimation-in-the-browser-with-tensorflow-js-7dd0bc881cd5
 */
export function drawHeatMapValues(heatMapValues, outputStride, canvas, color) {
    const ctx = canvas.getContext('2d');
    const radius = 5;
    const scaledValues = heatMapValues.mul(tf.scalar(outputStride, 'int32'));

    drawPoints(ctx, scaledValues, radius, color);
}

/**
 * Used by the drawHeatMapValues method to draw heatmap points on to
 * the canvas
 */
function drawPoints(ctx, points, radius, color) {
    const data = points.buffer().values;

    for (let i = 0; i < data.length; i += 2) {
        const pointY = data[i];
        const pointX = data[i + 1];

        if (pointX !== 0 && pointY !== 0) {
            ctx.beginPath();
            ctx.arc(pointX, pointY, radius, 0, 2 * Math.PI);
            ctx.fillStyle = color;
            ctx.fill();
        }
    }
}